import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

type TurnstileApi = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      action?: string;
      appearance?: "always" | "execute" | "interaction-only";
      size?: "normal" | "flexible" | "compact";
      theme?: "light" | "dark" | "auto";
      callback: (token: string) => void;
      "error-callback": (code: string) => boolean;
      "expired-callback": () => void;
      "timeout-callback": () => void;
      "unsupported-callback": () => void;
    },
  ) => string;
  remove: (widgetId: string) => void;
  reset: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export type TurnstileHandle = {
  takeToken: () => Promise<string>;
  reset: () => void;
};

type PendingToken = {
  resolve: (token: string) => void;
  reject: (error: Error) => void;
};

const SCRIPT_ID = "cloudflare-turnstile-script";
let scriptPromise: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const complete = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error("The security check could not be loaded."));
    };
    const fail = () =>
      reject(
        new Error(
          "The security check was blocked. Disable content blockers for this site and try again.",
        ),
      );
    const existing = document.getElementById(SCRIPT_ID) as
      | HTMLScriptElement
      | null;
    if (existing) {
      existing.addEventListener("load", complete, { once: true });
      existing.addEventListener("error", fail, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.addEventListener("load", complete, { once: true });
    script.addEventListener("error", fail, { once: true });
    document.head.appendChild(script);
  });

  return scriptPromise;
}

export const TurnstileWidget = forwardRef<
  TurnstileHandle,
  { siteKey?: string }
>(function TurnstileWidget({ siteKey }, ref) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const tokenRef = useRef<string | null>(null);
  const pendingRef = useRef<PendingToken | null>(null);
  const [status, setStatus] = useState<
    "loading" | "checking" | "ready" | "error"
  >("loading");
  const [message, setMessage] = useState("Loading secure verification…");

  function resetWidget() {
    tokenRef.current = null;
    if (window.turnstile && widgetIdRef.current) {
      window.turnstile.reset(widgetIdRef.current);
      setStatus("checking");
      setMessage("Running a fresh security check…");
    }
  }

  useImperativeHandle(
    ref,
    () => ({
      takeToken: () => {
        if (!siteKey)
          return Promise.reject(
            new Error("The upload security check is not configured."),
          );
        if (tokenRef.current) {
          const token = tokenRef.current;
          tokenRef.current = null;
          return Promise.resolve(token);
        }
        if (pendingRef.current)
          return Promise.reject(
            new Error("A security check is already in progress."),
          );
        if (status === "error") resetWidget();
        return new Promise<string>((resolve, reject) => {
          pendingRef.current = { resolve, reject };
        });
      },
      reset: resetWidget,
    }),
    [siteKey, status],
  );

  useEffect(() => {
    if (!siteKey) {
      setStatus("error");
      setMessage("Upload security is not configured yet.");
      return;
    }

    let cancelled = false;
    setStatus("loading");
    setMessage("Loading secure verification…");

    void loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) return;
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: "upload_authorize",
          appearance: "always",
          size: "flexible",
          theme: "light",
          callback: (token) => {
            if (pendingRef.current) {
              const pending = pendingRef.current;
              pendingRef.current = null;
              pending.resolve(token);
            } else {
              tokenRef.current = token;
            }
            setStatus("ready");
            setMessage("Security check complete.");
          },
          "error-callback": () => {
            const error = new Error(
              "The security check failed. Check your connection and try again.",
            );
            pendingRef.current?.reject(error);
            pendingRef.current = null;
            tokenRef.current = null;
            setStatus("error");
            setMessage(error.message);
            return true;
          },
          "expired-callback": () => {
            tokenRef.current = null;
            setMessage("Refreshing the security check…");
            queueMicrotask(resetWidget);
          },
          "timeout-callback": () => {
            tokenRef.current = null;
            setMessage("Refreshing the security check…");
            queueMicrotask(resetWidget);
          },
          "unsupported-callback": () => {
            const error = new Error(
              "This browser cannot run the upload security check.",
            );
            pendingRef.current?.reject(error);
            pendingRef.current = null;
            setStatus("error");
            setMessage(error.message);
          },
        });
        setStatus("checking");
        setMessage("Checking this browser…");
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const safeError =
          error instanceof Error
            ? error
            : new Error("The security check could not be loaded.");
        pendingRef.current?.reject(safeError);
        pendingRef.current = null;
        setStatus("error");
        setMessage(safeError.message);
      });

    return () => {
      cancelled = true;
      pendingRef.current?.reject(
        new Error("The security check was interrupted."),
      );
      pendingRef.current = null;
      tokenRef.current = null;
      if (window.turnstile && widgetIdRef.current)
        window.turnstile.remove(widgetIdRef.current);
      widgetIdRef.current = null;
    };
  }, [siteKey]);

  return (
    <div className={`turnstile-widget turnstile-${status}`}>
      <div ref={containerRef} className="turnstile-container" />
      <p role={status === "error" ? "alert" : "status"}>{message}</p>
    </div>
  );
});
